/**
 * LeadsKanban - the Leads board: a SAVED VIEW over accounts and sites, not a
 * new table (Paul: "I don't want the same company entered in two places").
 *
 * Cards are sites plus site-less accounts that are still Leads (a parent with
 * sites derives its stage from them, so it has no card). views/LeadsView
 * builds and filters the cards (components/crm/leadsBoard.js) and shares them
 * with the Table view; this component only lays them out in the five stage
 * columns. Dragging a card to another column PUTs the stage; the server
 * stamps the timestamp, appends stage history, and can refuse the move (the
 * Discovery Meeting Complete meeting gate) - the card then stays put because
 * columns are always drawn from the saved records.
 *
 * "Make Account" (Paul, 10/21/26) on a card switches the record's Designation
 * to Account - tracked, not a lead - so it leaves the board; LeadsView saves
 * it and offers Undo.
 */
import { useState } from 'react';
import { KANBAN_STAGES, STAGE_COLORS } from '../../data/crmStages';
import { fmtDate } from '../../utils/format';

const COL_BG = '#F7F8F5';
const tag = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 4,
  fontSize: 8.5,
  fontWeight: 800,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

export default function LeadsKanban({ cards, editable = false, onOpen, onStageChange, onMakeAccount }) {
  const [dragId, setDragId] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  const drop = (stage) => {
    if (!dragId || !editable) return;
    const card = cards.find((c) => c.id === dragId);
    setDragId(null);
    // compare the column, not the stored value: an old-stage card dropped
    // back where it already shows is not a change
    if (card && card.column !== stage) onStageChange(card, stage);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${KANBAN_STAGES.length}, 1fr)`, gap: 10, alignItems: 'start' }}>
      {KANBAN_STAGES.map((stage) => {
        const col = cards.filter((c) => c.column === stage);
        return (
          <div
            key={stage}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => drop(stage)}
            style={{ background: COL_BG, border: '1px solid #E8EBE3', borderRadius: 10, minHeight: 220 }}
          >
            <div
              style={{
                padding: '9px 12px',
                borderBottom: `2px solid ${STAGE_COLORS[stage]}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 9.5, fontWeight: 800, color: STAGE_COLORS[stage], letterSpacing: 0.6, textTransform: 'uppercase' }}>
                {stage}
              </span>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#999' }}>{col.length}</span>
            </div>
            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {col.map((c) => (
                <div
                  key={c.id}
                  draggable={editable}
                  onDragStart={() => setDragId(c.id)}
                  onDragEnd={() => setDragId(null)}
                  onClick={() => onOpen(c)}
                  style={{
                    background: '#fff',
                    border: '1px solid #E4E8DE',
                    borderRadius: 8,
                    padding: '9px 11px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#0D0D0D' }}>{c.name}</div>
                  {c.parentName && <div style={{ fontSize: 9.5, color: '#7B1FA2', fontWeight: 600 }}>{c.parentName} · site</div>}
                  {(c.nda || c.review) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                      {c.nda && (
                        <span
                          style={{ ...tag, background: c.nda.bg, color: c.nda.fg }}
                          title={c.nda.from ? `from ${c.nda.from}` : undefined}
                        >
                          {c.nda.label}
                        </span>
                      )}
                      {c.review && (
                        <span
                          style={{ ...tag, background: '#FFF3E0', color: '#E65100' }}
                          title="Stage set by the migration - needs a review"
                        >
                          review
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 9, color: '#888' }}>
                    <span>{c.owner || '-'}</span>
                    <span style={{ color: c.days > 45 ? '#B71C1C' : '#888', fontWeight: c.days > 45 ? 800 : 500 }}>
                      {c.days == null ? '' : `${c.days}d in stage`}
                    </span>
                  </div>
                  <div style={{ marginTop: 3, fontSize: 9, color: c.opps.open || c.opps.closed ? '#555' : '#bbb' }}>
                    Opps: <b>{c.opps.open}</b> open / <b>{c.opps.closed}</b> closed
                  </div>
                  {c.nextDue && (
                    <div style={{ marginTop: 3, fontSize: 9, fontWeight: 700, color: c.nextDue < today ? '#B71C1C' : '#1565C0' }}>
                      next task {fmtDate(c.nextDue)}
                    </div>
                  )}
                  {(c.hasTask === false || (editable && onMakeAccount)) && (
                    <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {c.hasTask === false && (
                        <span
                          style={{ ...tag, background: '#FFEBEE', color: '#B71C1C' }}
                          title="No open task - open the lead and add its next step"
                        >
                          No next step
                        </span>
                      )}
                      {editable && onMakeAccount && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMakeAccount(c);
                          }}
                          title="Not a business-development lead: make it an Account (tracked, off the Leads board and lead counts)"
                          style={{
                            marginLeft: 'auto',
                            padding: '1px 7px',
                            borderRadius: 4,
                            border: '1px solid #CFD8DC',
                            background: '#fff',
                            color: '#455A64',
                            fontSize: 8.5,
                            fontWeight: 800,
                            cursor: 'pointer',
                            fontFamily: 'Montserrat',
                          }}
                        >
                          Make Account
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {col.length === 0 && <div style={{ fontSize: 10, color: '#bbb', textAlign: 'center', padding: 14 }}>empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
